import React, { useState } from 'react';
import { US_STATES, isValidState } from '../constants/states';
import {
  isValidLicensePlate,
  sanitizeLicensePlateInput,
  PLATE_MAX_LENGTH,
  hasBlockedPlateChars,
  hasMaliciousPattern
} from '../utils/validation';

function SearchBar({ onSearch }) {
  const [plate, setPlate] = useState('');
  const [state, setState] = useState('');
  const [plateError, setPlateError] = useState('');
  const [stateError, setStateError] = useState('');

  const handlePlateChange = (event) => {
    const rawValue = event.target.value || '';
    const normalizedValue = sanitizeLicensePlateInput(rawValue);
    const hasInvalidChars = /[^a-zA-Z0-9-]/.test(rawValue) || hasBlockedPlateChars(rawValue);

    setPlate(normalizedValue);
    if (hasInvalidChars) {
      setPlateError('Only letters, numbers, and hyphens are allowed');
    } else if (plateError === 'Only letters, numbers, and hyphens are allowed') {
      setPlateError('');
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setPlateError('');
    setStateError('');

    const trimmedPlate = plate.trim();
    const trimmedState = state.trim().toUpperCase();

    if (!trimmedPlate) {
      setPlateError('License plate is required');
      return;
    }

    if (hasMaliciousPattern(trimmedPlate)) {
      setPlateError('Input rejected due to unsafe content');
      return;
    }

    if (!isValidLicensePlate(trimmedPlate)) {
      setPlateError('License plate must be 1-10 characters using letters, numbers, or hyphens');
      return;
    }

    if (!trimmedState) {
      setStateError('State is required');
      return;
    }

    if (!isValidState(trimmedState)) {
      setStateError('Invalid state');
      return;
    }

    onSearch(trimmedPlate, trimmedState);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row sm:gap-4 sm:items-start">
      <div className="flex-1 min-w-0">
        <label htmlFor="plate" className="block text-xs font-semibold uppercase tracking-[0.2em] leading-none text-slate-500">License Plate</label>
        <div className="mt-1 sm:mt-2">
          <input
            id="plate"
            type="text"
            value={plate}
            onChange={handlePlateChange}
            placeholder="e.g. ABC123"
            maxLength={PLATE_MAX_LENGTH}
            className={`h-12 w-full rounded-3xl border bg-white px-4 py-0 text-sm leading-none text-slate-900 shadow-sm outline-none transition ${
              plateError
                ? 'border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-200'
                : 'border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-200'
            }`}
          />
        </div>
        <p className={`mt-0.5 min-h-[14px] text-xs ${plateError ? 'text-rose-600' : 'text-transparent'}`}>
          {plateError || 'placeholder'}
        </p>
      </div>
      <div className="flex-1 min-w-0">
        <label htmlFor="state" className="block text-xs font-semibold uppercase tracking-[0.2em] leading-none text-slate-500">State</label>
        <div className="relative mt-1 sm:mt-2">
          <select
            id="state"
            value={state}
            onChange={(event) => setState(event.target.value)}
            className={`h-12 w-full appearance-none rounded-3xl border bg-white px-4 py-0 pr-14 text-sm leading-none text-slate-900 shadow-sm outline-none transition ${
              stateError
                ? 'border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-200'
                : 'border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-200'
            }`}
          >
            <option value="">Select a state</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-5 flex items-center text-slate-500">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
        <p className={`mt-0.5 min-h-[14px] text-xs ${stateError ? 'text-rose-600' : 'text-transparent'}`}>
          {stateError || 'placeholder'}
        </p>
      </div>
      <div className="flex w-full flex-col sm:min-w-fit sm:w-auto">
        <div className="hidden sm:block min-h-[20px]" aria-hidden="true"></div>
        <button
          type="submit"
          className="inline-flex h-12 w-full items-center justify-center rounded-3xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-lg shadow-slate-200/70 transition hover:bg-slate-800 sm:w-auto"
        >
          Search
        </button>
        <div className="mt-0.5 hidden sm:block min-h-[16px]" aria-hidden="true"></div>
      </div>
    </form>
  );
}

export default SearchBar;
